/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { useCallback, useState } from "react";

import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { Nullable } from "@simplyk/common";
import { useRouter } from "next/router";

import { isProduction } from "../../../common/constants/env";
import { useCurrentUserContext } from "../../../common/contexts/CurrentUserContext";
import { buildAdminMagicLoginUrl } from "../../../common/helpers/adminMagicLogin";

import { DevContextCopyProps } from "./types";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { Check } from "@/icons/filled";
import { Copy } from "@/icons/outlined";

const DevContextCopyContent = ({ formId }: DevContextCopyProps) => {
  const theme = useTheme();
  const [isCopied, setIsCopied] = useState(false);
  const { currentUser, organization } = useCurrentUserContext();
  const router = useRouter();
  const media = useMediaQuery();

  const copyLink = useCallback(() => {
    let timeout: Nullable<NodeJS.Timeout> = null;
    const route = router.asPath;
    const payload = {
      organizationId: organization?.id,
      organizationName: organization?.name,
      userId: currentUser?.id,
      email: currentUser?.email,
      route,
      magicLogin: process.browser
        ? buildAdminMagicLoginUrl({
            origin: window.location.origin,
            organizationId: organization?.id,
            email: currentUser?.email,
            route,
          })
        : null,
      connectedAsAdministrator: currentUser?.connectedAsAdministrator,
      screen: Object.entries(media)
        .filter(([key, value]) => value && key !== "value")
        .map(([key]) => key),
      agents: process.browser ? window.navigator.appVersion : "",
      timestamp: new Date().getTime(),
      formId,
    };

    try {
      navigator.clipboard.writeText(
        "Dev context \n" +
          Object.entries(payload)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join("\n")
      );
      setIsCopied(true);
      timeout = setTimeout(() => {
        setIsCopied(false);
      }, 800);
    } catch {
      return;
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    currentUser?.connectedAsAdministrator,
    currentUser?.email,
    currentUser?.id,
    formId,
    media,
    organization?.id,
    organization?.name,
    router.asPath,
  ]);

  return (
    <Grid
      container
      onClick={copyLink}
      sx={{
        cursor: "pointer",
        height: size,
        width: size,
        backgroundColor: theme.palette.secondary.main,
        position: "fixed",
        top: -size / 2,
        right: -size / 2,
        opacity: 0.5,
        zIndex: 100000000,
        borderRadius: "5px",
        transition: "top 0.2s ease, right 0.2s ease",
        "&:hover": {
          top: 0,
          right: 0,
          "& .dev-context-icon": {
            display: "block",
          },
        },
      }}
    >
      <DashboardIcon
        size="small"
        sx={{
          margin: 0.5,
          display: "none",
        }}
        className="dev-context-icon"
      >
        {isCopied ? <Check /> : <Copy />}
      </DashboardIcon>
    </Grid>
  );
};

const size = 25;

export const DevContextCopy = (props: DevContextCopyProps) => {
  const { currentUser } = useCurrentUserContext();
  if (!isProduction) {
    return <DevContextCopyContent {...props} />;
  }
  if (currentUser?.connectedAsAdministrator) {
    return <DevContextCopyContent {...props} />;
  }
  return null;
};
